/**
 * IntentPaywall - Contextual paywall that preserves user momentum
 * 
 * Design Principles:
 * - Presented as a "continuation step", not a blocker
 * - Shows the specific feature user wanted to access
 * - Maintains emotional momentum with encouraging copy
 * - Seamless post-subscription action resumption
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Sparkles, ArrowRight, Check, Zap } from 'lucide-react';
import { usePremiumAction } from '@/contexts/PremiumActionContext';

// Guapo character for personality
const GUAPO_IMAGE = 'https://customer-assets.emergentagent.com/job_budget-buddy-4768/artifacts/qhory5fo_Guap.png';

export default function IntentPaywall() {
  const { 
    showPaywall, 
    pendingAction, 
    handlePaywallCancel, 
    navigateToPricing 
  } = usePremiumAction();

  if (!showPaywall || !pendingAction) return null;

  const { metadata } = pendingAction;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && handlePaywallCancel()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
        >
          {/* Header with Guapo */}
          <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 p-6 text-white relative overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute -right-8 -top-8 w-32 h-32 bg-white rounded-full" />
              <div className="absolute -left-4 -bottom-4 w-24 h-24 bg-white rounded-full" />
            </div>
            
            <button
              onClick={handlePaywallCancel}
              className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
              data-testid="close-paywall-btn"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="relative flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <span className="text-4xl">{metadata.icon}</span>
              </div>
              <div className="flex-1">
                <p className="text-emerald-100 text-sm font-medium">Almost there! 🎯</p>
                <h2 className="text-2xl font-bold">{metadata.title}</h2>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Guapo encouragement */}
            <div className="flex items-start gap-3 mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-200">
              <img 
                src={GUAPO_IMAGE} 
                alt="Guapo" 
                className="w-12 h-12 rounded-full border-2 border-emerald-300"
              />
              <div>
                <p className="font-bold text-emerald-900 text-sm">Guapo says:</p>
                <p className="text-emerald-700 text-sm">
                  "You're making a smart move! 💪 Upgrade to unlock <strong>{metadata.title}</strong> and level up your financial game!"
                </p>
              </div>
            </div>

            {/* Feature description */}
            <p className="text-gray-600 mb-6">
              {metadata.description}
            </p>

            {/* Premium benefits */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-500" />
                Premium unlocks:
              </h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span><strong>Subscription Hunter</strong> - Find hidden recurring charges</span>
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span><strong>Bank Sync</strong> - Auto-import all transactions</span>
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span><strong>AI Insights</strong> - Personalized money advice</span>
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span><strong>Full History</strong> - Unlimited data access</span>
                </li>
              </ul>
            </div>

            {/* Pricing hint */}
            <div className="text-center mb-4">
              <p className="text-gray-500 text-sm">
                Starting at just <span className="font-bold text-gray-900">$6.99/month</span>
              </p>
              <p className="text-emerald-600 text-xs font-medium flex items-center justify-center gap-1">
                <Zap className="w-3 h-3" />
                Cancel anytime • 7-day free trial
              </p>
            </div>

            {/* CTAs */}
            <div className="space-y-3">
              <button
                onClick={navigateToPricing}
                className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl hover:from-emerald-600 hover:to-teal-600 transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/30"
                data-testid="view-plans-btn"
              >
                <Sparkles className="w-5 h-5" />
                View Plans & Start Free Trial
                <ArrowRight className="w-5 h-5" />
              </button>
              
              <button
                onClick={handlePaywallCancel}
                className="w-full py-3 text-gray-500 font-medium hover:text-gray-700 transition-colors text-sm"
                data-testid="maybe-later-btn"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
